/**
 * Types for the org enrollment API.
 *
 * Source: bankingbridge listing_flyer/.docs/ORG_ENROLL_API.md
 *
 * Kept colocated with the page (not src/types/) because they are not used
 * anywhere else in the app and we want the public/anonymous future variant
 * of this page to have a self-contained module to lift out.
 */

export type EnrollmentStepName =
  | "org"
  | "brand"
  | "lo"
  | "rateflow"
  | "profiles"
  | "workflow"
  | "embeds"
  | "billing"

export const ENROLLMENT_STEP_ORDER: EnrollmentStepName[] = [
  "org",
  "brand",
  "lo",
  "rateflow",
  "profiles",
  "workflow",
  "embeds",
  "billing"
]

export const ENROLLMENT_STEP_LABELS: Record<EnrollmentStepName, string> = {
  org: "Create organization",
  brand: "Create brand",
  lo: "Create first loan officer",
  rateflow: "Set up rateflow",
  profiles: "Create pricing profiles",
  workflow: "Create lead workflow",
  embeds: "Create standard embeds",
  billing: "Set up billing"
}

export type EnrollmentStatus = "started" | "complete" | "partial" | "failed"
export type StepStatus =
  | "pending"
  | "started"
  | "success"
  | "failed"
  | "skipped"

export interface EnrollmentStep {
  status: StepStatus
  /** node id, OR a count for `profiles` and `embeds` (footgun) */
  id: number | null
  /** machine code (e.g. "duplicate", "validation") */
  error: string | null
  /** human message — present only when status="failed" */
  error_message?: string
  details: Record<string, unknown> | null
}

export interface EnrollmentResponse {
  enrollment_id: number
  status: EnrollmentStatus
  org_id: number | null
  brand_id: number | null
  loid: number | null
  retry_count: number
  /** unix seconds */
  started_at: number
  /** unix seconds */
  updated_at: number
  steps: Record<EnrollmentStepName, EnrollmentStep>
  failed_step?: EnrollmentStepName
  /** always present — render as headline */
  message: string
}

export interface ValidationError {
  status: "error"
  error: "validation"
  message: string
  /** Nested fields use dot notation: "first_user.email" */
  fields: Record<string, { code: string; message: string }>
}

export type GenericErrorCode =
  | "idempotency_conflict"
  | "not_found"
  | "invalid_id"
  | "in_progress"
  | "retry_limit_exceeded"
  | "state_corrupt"
  | "insert_failed"

export interface GenericError {
  status: "error"
  error: GenericErrorCode
  message: string
  enrollment_id?: number
}

export type BillingLevel = "growth" | "core" | "launch" | "api" | "custom"

export type EnrollApiMode =
  | "polly"
  | "lender_price"
  | "loan_sifter"
  | "optimal_blue"
  | "mortech"
  | "mortgage_bot"
  | "loanpass"

export interface EnrollFirstUser {
  first_name: string
  last_name: string
  email: string
  nmls: string
  phone?: string
  /** NEVER stored, NEVER returned, NOT replayable on retry */
  password?: string
}

export interface BrandSettings {
  disclaimer?: string
  msa_template?: string
  about?: string
  payment_details_disclaimer?: string
  lead_capture_disclaimer?: string
  loan_calc_disclaimer?: string
  subscription_email_disclaimer?: string
  third_party_fees_disclaimer?: string
  ob_channel_id?: string
  zillow_brand_name?: string
  google_place_id?: string
  experience_id?: string
  yelp_id?: string
  privacy_policy_url?: string
  terms_and_conditions_url?: string
  non_bank?: boolean
  disable_subscription_emails?: boolean
  disable_itemized_fees?: boolean
  disable_receipt_emails?: boolean
}

export interface EnrollRequest {
  idempotency_key: string
  company_name: string
  company_nmls: string
  website: string
  org_name?: string
  api_mode?: EnrollApiMode
  primary_color?: string
  secondary_color?: string
  logo_url?: string
  brand_settings?: BrandSettings
  first_user: EnrollFirstUser
  billing_level?: BillingLevel
  /** Amount in dollars (e.g. 950 = $950.00). Omit to skip setup fee. */
  setup_fee_amount?: number
  /** Only needed if billing contact differs from the first user. */
  billing_email?: string
}
